import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Plus, Building2, ClipboardList, FileText, GitBranch, Lock, ArrowUpRight, ArrowRight, CheckCircle2, Clock3 } from "lucide-react";
import { getAuditType } from "@/lib/auditTypes";

function groupByLineage(audits) {
  const groups = new Map();
  for (const a of audits) {
    const key = a.lineage_id || a.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }
  // sort versions desc within each lineage
  for (const arr of groups.values()) {
    arr.sort((x, y) => (y.version || 1) - (x.version || 1));
  }
  // build array of lineages sorted by latest updated_at
  return Array.from(groups.values()).sort((a, b) => {
    const ax = a[0]?.updated_at || "";
    const bx = b[0]?.updated_at || "";
    return bx.localeCompare(ax);
  });
}

function LineageCard({ lineage }) {
  const latest = lineage[0];
  const history = lineage.slice(1);
  return (
    <div data-testid={`lineage-${latest.lineage_id || latest.id}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-slate-300 transition-colors duration-200">
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <Link to={`/audit/${latest.id}`} className="font-semibold text-slate-900 hover:text-blue-600 transition-colors duration-200 text-base leading-snug block truncate" data-testid={`lineage-title-${latest.lineage_id}`}>
              {latest.title}
            </Link>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {(latest.types || [latest.type]).filter(Boolean).map((t) => {
                const m = getAuditType(t);
                return <span key={t} className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${m.badge_bg} ${m.badge_text}`}>{m.label}</span>;
              })}
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-plex">v{latest.version || 1}</span>
              {latest.frozen && (
                <span className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <Lock className="h-2.5 w-2.5" /> Storico
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="font-display font-black text-2xl text-slate-900">{latest.score}%</div>
            <div className={`text-[10px] uppercase tracking-wider ${latest.status === "completed" ? "text-emerald-600" : "text-amber-600"}`}>
              {latest.status === "completed" ? "Completato" : "In corso"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="font-mono-plex">{latest.updated_at?.slice(0, 10)}</span>
          <span>·</span>
          <span>{lineage.length} {lineage.length === 1 ? "revisione" : "revisioni"}</span>
        </div>
      </div>
      {history.length > 0 && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Storico</div>
          <div className="space-y-1">
            {history.map((h) => (
              <Link key={h.id} to={`/audit/${h.id}`} className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 py-0.5 transition-colors duration-200" data-testid={`history-item-${h.id}`}>
                <Lock className="h-2.5 w-2.5" />
                <span className="font-mono-plex">v{h.version || 1}</span>
                <span className="text-slate-400">·</span>
                <span className="font-mono-plex">{h.updated_at?.slice(0, 10)}</span>
                <span className="text-slate-400">·</span>
                <span className="font-semibold">{h.score}%</span>
                <ArrowUpRight className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CompanyPanel({ company, audits }) {
  const lineages = useMemo(() => groupByLineage(audits), [audits]);
  const completed = audits.filter((a) => a.status === "completed").length;
  const avgScore = audits.length ? (audits.reduce((s, a) => s + (a.score || 0), 0) / audits.length).toFixed(1) : "0.0";

  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display font-black text-2xl">{company.name}</h2>
            <div className="text-sm text-slate-500 mt-1">
              {company.sector && <>{company.sector} · </>}
              {company.vat && <span className="font-mono-plex">{company.vat}</span>}
            </div>
            {company.notes && <p className="text-sm text-slate-600 mt-2 max-w-lg">{company.notes}</p>}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="font-display font-black text-2xl">{lineages.length}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Progetti audit</div>
            </div>
            <div className="text-center">
              <div className="font-display font-black text-2xl">{audits.length}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Revisioni tot.</div>
            </div>
            <div className="text-center">
              <div className="font-display font-black text-2xl text-emerald-600">{completed}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Completati</div>
            </div>
            <div className="text-center">
              <div className="font-display font-black text-2xl">{avgScore}%</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Score medio</div>
            </div>
            <Link to={`/audit/nuovo?company=${company.id}`}>
              <Button className="h-10 bg-slate-900 hover:bg-slate-800" data-testid={`new-audit-for-${company.id}`}>
                <Plus className="h-4 w-4" /> Nuovo audit
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {lineages.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-16 text-center">
          <ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <div className="font-display font-bold text-lg mb-2">Nessun audit ancora</div>
          <p className="text-slate-500 text-sm mb-6">Crea il primo audit per {company.name}.</p>
          <Link to={`/audit/nuovo?company=${company.id}`}>
            <Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" /> Nuovo audit</Button>
          </Link>
        </div>
      ) : (
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-3">Storico audit ({lineages.length} progetti)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {lineages.map((l) => (
              <LineageCard key={l[0].lineage_id || l[0].id} lineage={l} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const BUILT_IN_FRAMEWORKS = ["gdpr", "nis2", "iso27001", "dora", "ai_act"];

export default function Dashboard() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [auditsByCompany, setAuditsByCompany] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  const [recentAudits, setRecentAudits] = useState([]);
  const [stats, setStats] = useState({ companies: 0, total_audits: 0, lineages: 0, completed: 0, in_progress: 0, by_type: {}, avg_score: 0 });

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, aRes, sRes] = await Promise.all([
          api.get("/companies"),
          api.get("/audits"),
          api.get("/stats"),
        ]);
        setCompanies(cRes.data);
        setRecentAudits(aRes.data.slice(0, 6));
        setStats(sRes.data);
        const grouped = {};
        for (const a of aRes.data) {
          const cid = a.company_id;
          (grouped[cid] ||= []).push(a);
        }
        setAuditsByCompany(grouped);
        if (cRes.data.length && activeTab === "overview") {
          // stay on overview by default
        }
      } catch (err) {
        console.error("Dashboard load failed:", err?.response?.data?.detail || err.message);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-5 md:p-10 max-w-7xl mx-auto">
      <div className="mb-8 rounded-2xl bg-slate-950 p-6 md:p-8 text-white shadow-xl shadow-slate-200/60">
        <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-300 mb-2">Workspace compliance</div>
          <h1 className="font-display font-black text-4xl md:text-5xl">Buongiorno {user?.full_name?.split(" ")[0]}</h1>
          <p className="text-slate-300 mt-3 text-sm max-w-xl">Tieni sotto controllo il portafoglio audit, lo stato delle verifiche e le prossime attività del tuo team.</p>
        </div>
        <Link to="/audit/nuovo" data-testid="new-audit-btn">
          <Button className="h-11 bg-white text-slate-950 hover:bg-sky-50 px-5">
            <Plus className="h-4 w-4" /> Nuovo Audit
          </Button>
        </Link>
        </div>
        <div className="mt-8 flex flex-wrap gap-2 text-xs text-slate-300"><span className="rounded-full border border-slate-700 px-3 py-1.5">{companies.length} aziende attive</span><span className="rounded-full border border-slate-700 px-3 py-1.5">{stats.in_progress} audit in corso</span><span className="rounded-full border border-slate-700 px-3 py-1.5">{stats.completed} completati</span></div>
      </div>

      {companies.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-16 text-center">
          <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
          <div className="font-display font-bold text-lg mb-2">Nessuna azienda registrata</div>
          <p className="text-slate-500 text-sm mb-6">Aggiungi la prima azienda per iniziare a compilare audit.</p>
          <Link to="/aziende"><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" /> Vai alle aziende</Button></Link>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 h-auto p-1 bg-slate-100 flex flex-wrap gap-1" data-testid="companies-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Panoramica
            </TabsTrigger>
            {companies.map((c) => (
              <TabsTrigger key={c.id} value={c.id} data-testid={`tab-company-${c.id}`} className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <Building2 className="h-3.5 w-3.5 mr-1.5 opacity-70" />
                {c.name}
                <span className="ml-2 text-[10px] font-mono-plex text-slate-400">
                  {(auditsByCompany[c.id] || []).length}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="mt-0 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <StatBlock label="Aziende" value={stats.companies} icon={Building2} accent="bg-slate-900" />
              <StatBlock label="Progetti audit" value={stats.lineages} icon={GitBranch} accent="bg-blue-600" />
              <StatBlock label="Completati" value={stats.completed} icon={CheckCircle2} accent="bg-emerald-600" />
              <StatBlock label="Score medio" value={`${stats.avg_score}%`} icon={FileText} accent="bg-amber-500" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-4">
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-5"><div><div className="text-xs uppercase tracking-[0.15em] text-slate-400">Copertura normativa</div><h2 className="font-display font-bold text-xl mt-1">Framework ufficiali</h2></div><Link to="/app/template" className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">Template <ArrowRight className="h-3 w-3" /></Link></div>
                <div className="space-y-3">
                {BUILT_IN_FRAMEWORKS.map((tp) => {
                  const count = stats.by_type?.[tp] || 0;
                  const meta = getAuditType(tp);
                  return (
                    <div key={tp} className="flex items-center gap-3">
                      <span className={`h-8 w-8 rounded-lg ${meta.color_bg} flex items-center justify-center text-white text-xs font-bold`}>{meta.label.slice(0, 2)}</span>
                      <div className="min-w-0 flex-1"><div className="flex items-center justify-between text-sm"><span className="font-semibold text-slate-800">{meta.label}</span><span className="font-mono-plex text-slate-500">{count} audit</span></div><div className="mt-1.5"><Progress value={stats.total_audits ? (count / stats.total_audits) * 100 : 0} className="h-1.5" /></div></div>
                    </div>
                  );
                })}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="mb-5"><div className="text-xs uppercase tracking-[0.15em] text-slate-400">Attività</div><h2 className="font-display font-bold text-xl mt-1">Audit recenti</h2></div>
                {recentAudits.length ? <div className="space-y-3">{recentAudits.slice(0, 4).map((audit) => <Link key={audit.id} to={`/audit/${audit.id}`} className="group flex items-center gap-3 rounded-lg border border-slate-100 p-3 hover:border-blue-200 hover:bg-blue-50/40"><div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center"><Clock3 className="h-4 w-4 text-slate-500" /></div><div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold text-slate-800 group-hover:text-blue-700">{audit.title}</div><div className="truncate text-xs text-slate-500">{audit.company_name}</div></div><div className="text-right"><div className="font-mono-plex text-sm font-bold text-slate-800">{audit.score}%</div><div className="text-[10px] text-slate-400">{audit.updated_at?.slice(0, 10)}</div></div></Link>)}</div> : <div className="rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500">Nessuna attività recente.</div>}
                <Link to="/app/audit" className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800">Vedi tutti gli audit <ArrowRight className="h-3 w-3" /></Link>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-3">Aziende</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {companies.map((c) => {
                  const list = auditsByCompany[c.id] || [];
                  const lineages = groupByLineage(list);
                  const latest = list[0];
                  return (
                    <button
                      key={c.id}
                      onClick={() => setActiveTab(c.id)}
                      data-testid={`company-tile-${c.id}`}
                      className="text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-400 transition-all duration-200 hover:-translate-y-[1px]"
                    >
                      <div className="font-display font-bold text-base truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{c.sector || "Settore n/d"}</div>
                      <div className="mt-3 flex items-center justify-between text-xs">
                        <div className="text-slate-500">
                          <span className="font-mono-plex text-slate-900 font-semibold">{lineages.length}</span> progetti · {list.length} rev.
                        </div>
                        {latest && <span className="font-mono-plex text-slate-500">{latest.updated_at?.slice(0, 10)}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {companies.map((c) => (
            <TabsContent key={c.id} value={c.id} className="mt-0">
              <CompanyPanel company={c} audits={auditsByCompany[c.id] || []} />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}

function StatBlock({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
          <div className="mt-2 font-display font-black text-3xl text-slate-900">{value}</div>
        </div>
        <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent}`}>
          <Icon className="h-4 w-4 text-white" strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
}
